"use client";

import { useMemo, useCallback } from "react";
import { SlidersHorizontal } from "lucide-react";
import { useRouter } from "next/navigation";
import { useAppDispatch } from "@/app/lib/features/redux/redux.hook";
import useProductReview from "@/app/lib/features/productReview/productReview.hook";
import {
  toggleRatingFilter,
  toggleTopicFilter,
  clearRatingFilter,
  clearTopicFilter,
  setAppliedFilters,
  setShowFilterSidebar,
  setOpenFilterDropdown,
} from "@/app/lib/features/productReview/productReview.slice";
import { getProductReviewsList as getProductReviewsListThunk } from "@/app/lib/features/productReview/productReview.thunk";
import { ReviewFilterRowData } from "@/app/lib/features/productReview/productReview.types";
import FilterSelect from "../../elements/forms/filterSelect/FilterSelect";
import StarRating from "@/app/components/elements/StarRating";
import { trackGeneric } from "@/app/lib/tracking/interactions";

interface FilterSectionProps {
  filterRowData?: ReviewFilterRowData;
}

const FilterSection = ({ filterRowData }: FilterSectionProps) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const {
    appliedFilters,
    pendingFilters,
    currentProductSlug,
    openFilterDropdowns,
  } = useProductReview();

  const allFiltersButton = useMemo(() => {
    return filterRowData?.items.find((item) => item.type === "modal");
  }, [filterRowData]);

  const selectFilters = useMemo(() => {
    return filterRowData?.items.filter((item) => item.type === "select") || [];
  }, [filterRowData]);

  const applyFiltersLocal = useCallback(() => {
    if (!currentProductSlug) return;

    const params = new URLSearchParams();

    if (pendingFilters.rating && pendingFilters.rating.length > 0) {
      pendingFilters.rating.forEach((r) =>
        params.append("rating", r.toString()),
      );
    }

    if (pendingFilters.topic && pendingFilters.topic.length > 0) {
      pendingFilters.topic.forEach((t) => params.append("topic", t));
    }

    if (pendingFilters.seller_id) {
      params.set("seller_id", pendingFilters.seller_id.toString());
    }

    if (pendingFilters.search) {
      params.set("search", pendingFilters.search);
    }

    if (pendingFilters.sort) {
      params.set("sort", pendingFilters.sort);
    }

    const queryString = params.toString();
    const newUrl = queryString ? `?${queryString}` : window.location.pathname;

    // Use router.replace with scroll: false to prevent scrolling to top
    router.replace(newUrl, { scroll: false });

    dispatch(setAppliedFilters(pendingFilters));

    dispatch(
      getProductReviewsListThunk({
        productSlug: currentProductSlug,
        filters: { ...pendingFilters, page: 1, per_page: 15 },
      }),
    );
  }, [pendingFilters, router, dispatch, currentProductSlug]);

  const handleFilterChange = useCallback(
    (filterKey: string, value: string | number) => {
      if (filterKey === "topic") {
        dispatch(toggleTopicFilter(value.toString()));
      } else if (filterKey === "rating") {
        const valueNum =
          typeof value === "number" ? value : parseInt(value.toString());
        dispatch(toggleRatingFilter(valueNum));
      }
    },
    [dispatch],
  );

  const handleClearFilter = useCallback(
    (filterKey: string) => {
      if (filterKey === "topic") {
        dispatch(clearTopicFilter());
      } else if (filterKey === "rating") {
        dispatch(clearRatingFilter());
      }
    },
    [dispatch],
  );

  const handleApplyFilter = useCallback(() => {
    trackGeneric("review_filter_apply", "click");
    applyFiltersLocal();
    dispatch(setOpenFilterDropdown(null));
  }, [applyFiltersLocal, dispatch]);

  const handleFilterOpenChange = useCallback(
    (filterKey: string, isOpen: boolean) => {
      dispatch(setOpenFilterDropdown(isOpen ? filterKey : null));
    },
    [dispatch],
  );

  const hasAnyActiveFilter =
    (appliedFilters.rating && appliedFilters.rating.length > 0) ||
    (appliedFilters.topic && appliedFilters.topic.length > 0);

  if (!filterRowData) return null;

  return (
    <div className="md:bg-white md:border md:border-gray-200 md:rounded-lg md:p-4">
      {/* Mobilde 3 filtre yan yana sığmıyor (min-w-32 × 3 + boşluklar telefon
          genişliğini aşıyor); sıkıştırmak yerine yatay kaydırma. md ve üstünde
          eskisi gibi 3 kolonlu grid. */}
      <div className="flex gap-3 overflow-x-auto scrollbar-hide touch-pan-x md:grid md:grid-cols-3 md:overflow-visible">
        {allFiltersButton && (
          <button
            onClick={() => {
              trackGeneric("review_filter_all", "click");
              dispatch(setShowFilterSidebar(true));
            }}
            className={`shrink-0 whitespace-nowrap rounded-md border px-3 py-1 text-sm shadow-xs transition-[color,box-shadow,background-color] outline-none flex items-center justify-center cursor-pointer gap-2 ${
              hasAnyActiveFilter
                ? "border-black bg-gray-100 text-black font-medium"
                : "border-input bg-transparent hover:border-main/50 hover:ring-main/25 hover:ring-[3px] text-muted-foreground"
            }`}
          >
            <SlidersHorizontal
              className={`w-4 h-4 shrink-0 ${hasAnyActiveFilter ? "text-black" : "opacity-50"}`}
            />
            {allFiltersButton.text}
          </button>
        )}

        {selectFilters.map((filter) => {
          const selectedValues =
            filter.key === "topic"
              ? pendingFilters?.topic || []
              : filter.key === "rating"
                ? (pendingFilters?.rating || []).map((r) => r.toString())
                : [];

          const isActive =
            filter.key === "topic"
              ? Boolean(appliedFilters.topic && appliedFilters.topic.length > 0)
              : filter.key === "rating"
                ? Boolean(
                    appliedFilters.rating && appliedFilters.rating.length > 0,
                  )
                : false;

          const isOpen = openFilterDropdowns === filter.key;

          const options = (filter.options || []).map((opt) => ({
            id: opt.key,
            label: opt.text || "",
            count: opt.count,
            component:
              opt.rating !== undefined ? (
                <StarRating rating={opt.rating} size="sm" className="gap-1" />
              ) : undefined,
          }));

          return (
            <FilterSelect
              key={filter.key}
              placeholder={filter.text}
              options={options}
              selectedValues={selectedValues}
              onSelectionChange={(value) =>
                handleFilterChange(filter.key, value)
              }
              isOpen={isOpen}
              onOpenChange={(open) => handleFilterOpenChange(filter.key, open)}
              isActive={isActive}
              contentWidth={filter.key === "topic" ? "w-80" : undefined}
              showSearch={filter.key === "topic"}
              buttons={[
                {
                  label: "Temizle",
                  onClick: () => handleClearFilter(filter.key),
                  variant: "outline",
                },
                {
                  label: "Uygula",
                  onClick: handleApplyFilter,
                  variant: "default",
                },
              ]}
            />
          );
        })}
      </div>
    </div>
  );
};

export default FilterSection;
